import React from 'react';
import GameIcon from './GameIcon';

export default function WalletModal({ coins, loginStreak, dailyRewardAvailable, onClaimDaily, onClose, gameSettings = {}, dailyChallenges = {}, challengeDefs = [], onClaimChallenge }) {
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-3 z-[60] animate-modal-overlay">
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full max-h-[85vh] overflow-y-auto animate-modal-content">
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold text-gray-900 text-lg">{gameSettings.wallet_title || 'Wallet'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg">✕</button>
        </div>

        <div className="p-5 space-y-4">
          {/* Balance */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
            <p className="text-xs text-amber-600 mb-1">{gameSettings.wallet_balance_label || 'Your Balance'}</p>
            <p className="text-3xl font-bold text-amber-800"><GameIcon icon={gameSettings.icon_token || '🪙'} /> {coins}</p>
            <p className="text-[10px] text-amber-500 mt-1">{gameSettings.wallet_tokens_label || 'Tokens'}</p>
          </div>

          {/* Login Streak */}
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
            <p className="text-xs text-orange-600 mb-1">{gameSettings.wallet_streak_label || 'Login Streak'}</p>
            <p className="text-2xl font-bold text-orange-700"><GameIcon icon={gameSettings.icon_streak || '🔥'} /> {loginStreak || 0}</p>
            <p className="text-[10px] text-orange-500 mt-1">{gameSettings.wallet_days_row || `day${loginStreak !== 1 ? 's' : ''} in a row`}</p>
          </div>

          {/* Daily Reward */}
          {dailyRewardAvailable && (
            <button
              onClick={onClaimDaily}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-400 to-yellow-400 text-amber-900 font-bold text-sm hover:from-amber-500 hover:to-yellow-500 transition-colors shadow-sm"
            >
              {gameSettings.wallet_daily_button || 'Claim Daily Reward'} <GameIcon icon={gameSettings.icon_gift || '🎁'} />
            </button>
          )}
          {!dailyRewardAvailable && (
            <div className="text-center text-xs text-gray-400 py-1">
              {gameSettings.wallet_claimed_msg || 'Daily reward claimed — come back tomorrow!'}
            </div>
          )}

          {/* Daily Challenges */}
          {challengeDefs.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-600 mb-2">{gameSettings.wallet_challenges_title || 'Daily Challenges'}</p>
              <div className="space-y-2">
                {challengeDefs.map(ch => {
                  const progress = dailyChallenges[ch.key] || 0;
                  const pct = Math.min(100, Math.round((progress / ch.target) * 100));
                  const completed = progress >= ch.target;
                  const claimed = dailyChallenges.claimed?.[ch.key];
                  return (
                    <div key={ch.key} className={`rounded-xl border p-3 ${claimed ? 'bg-green-50 border-green-200' : completed ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-100'}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-base"><GameIcon icon={ch.icon} /></span>
                          <span className="text-xs font-medium text-gray-700">{ch.label.replace('{N}', ch.target)}</span>
                        </div>
                        <span className="text-[10px] font-bold text-amber-700"><GameIcon icon={gameSettings.icon_token || '🪙'} /> +{ch.reward}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${claimed ? 'bg-green-400' : completed ? 'bg-amber-400' : 'bg-[#2D6A4F]'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-[10px] text-gray-500 whitespace-nowrap">{Math.min(progress, ch.target)}/{ch.target}</span>
                      </div>
                      {completed && !claimed && (
                        <button
                          onClick={() => onClaimChallenge?.(ch.key, ch.reward)}
                          className="w-full mt-2 py-1.5 rounded-lg bg-amber-400 hover:bg-amber-500 text-amber-900 font-bold text-xs transition-colors"
                        >
                          {gameSettings.wallet_claim_challenge || 'Claim Reward!'} <GameIcon icon={gameSettings.icon_token || '🪙'} />
                        </button>
                      )}
                      {claimed && (
                        <p className="text-[10px] text-green-600 font-medium text-center mt-1">{gameSettings.wallet_challenge_claimed || 'Claimed!'} ✓</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Info */}
          <div className="bg-gray-50 rounded-xl p-3 text-xs text-gray-500 space-y-1">
            {(gameSettings.wallet_tips || [
              'Earn tokens by completing missions',
              'Save tokens by bargaining with merchants',
              'Daily login rewards increase with streaks'
            ]).map((tip, i) => (
              <p key={i}>• {tip}</p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
